<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { getTranslation } from '@/utils/data-display-utils';
import { constant } from '@/constant';
import { TimelineStep } from '@/store/timelineStep.interface';

import CircleIcon from './CircleIcon.vue';
import { Locales } from '@/store/language.interface';

const { t } = useI18n();

// PROPS
const props = defineProps<{
  item: TimelineStep;
  isModal?: boolean;
  languageEnabled?: Locales[];
}>();

// EMITS
const emit = defineEmits<{
  (e: 'see-more', id: number): void;
}>();

// REACTIVE STATE
const showSeeMore = ref(false);
const contentRef = ref<HTMLElement>();
const dynamicLineClamp = ref(5);
let resizeObserver: ResizeObserver | null = null;

// COMPUTED
const dynamicMaxLines = computed(() => dynamicLineClamp.value);

const statusText = computed(() => {
  const statusMap = {
    [constant.ProjectModule.TimelineStep.Status.Completed]: 'completed',
    [constant.ProjectModule.TimelineStep.Status.Current]: 'current',
    [constant.ProjectModule.TimelineStep.Status.Upcoming]: 'upcoming',
  };
  return t(`project-module.timeline.status.${statusMap[props.item.status]}`);
});

// FUNCTIONS
const calculateOptimalLineClamp = () => {
  if (!contentRef.value || props.isModal) return;

  const element = contentRef.value;
  const parent = element.parentElement;
  if (!parent) return;

  const parentHeight = parent.clientHeight;
  const buttonHeight = 40;
  const availableHeight = parentHeight - buttonHeight;

  const originalDisplay = element.style.display;
  const originalLineClamp = element.style.webkitLineClamp;
  const originalMaxHeight = element.style.maxHeight;

  element.style.display = 'block';
  element.style.webkitLineClamp = 'none';
  element.style.maxHeight = 'none';

  const naturalHeight = element.scrollHeight;

  element.style.display = originalDisplay;
  element.style.webkitLineClamp = originalLineClamp;
  element.style.maxHeight = originalMaxHeight;

  if (naturalHeight <= availableHeight) {
    dynamicLineClamp.value = 20;
    return;
  }

  const tempElement = document.createElement('div');
  tempElement.style.cssText = window.getComputedStyle(element).cssText;
  tempElement.style.position = 'absolute';
  tempElement.style.visibility = 'hidden';
  tempElement.style.height = 'auto';
  tempElement.style.maxHeight = 'none';
  tempElement.style.webkitLineClamp = 'none';
  tempElement.innerHTML = 'M';

  document.body.appendChild(tempElement);
  const singleLineHeight = tempElement.offsetHeight;
  document.body.removeChild(tempElement);

  const optimalLines = Math.floor(availableHeight / singleLineHeight);

  const clampedLines = Math.max(2, Math.min(15, optimalLines));

  dynamicLineClamp.value = clampedLines;
};

const checkOverflow = () => {
  if (!contentRef.value || props.isModal) return;

  const element = contentRef.value;
  const hasOverflow = element.scrollHeight > element.clientHeight;
  showSeeMore.value = hasOverflow;
};

const waitAndCheck = async () => {
  if (!props.item.title || !props.item.content) return;

  await nextTick();
  setTimeout(() => {
    calculateOptimalLineClamp();
    checkOverflow();
  }, 100);
};

const setupResizeObserver = () => {
  if (contentRef.value && contentRef.value.parentElement) {
    resizeObserver = new ResizeObserver(() => {
      calculateOptimalLineClamp();
      checkOverflow();
    });
    resizeObserver.observe(contentRef.value.parentElement);
  }
};

const cleanupResizeObserver = () => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
};

const handleSeeMoreClick = (event: Event) => {
  emit('see-more', props.item.id);
  (event.target as HTMLElement)?.blur();
};

// LIFECYCLE
onMounted(() => {
  waitAndCheck();
  setupResizeObserver();
});

onUnmounted(() => {
  cleanupResizeObserver();
});

// WATCHERS
watch([() => props.item], waitAndCheck);
watch(dynamicLineClamp, () => {
  nextTick(() => checkOverflow());
});

// COMPUTED
const iconMap = (item: number) =>
  computed(() => {
    switch (item) {
      case constant.ProjectModule.TimelineStep.Status.Completed:
        return { color: '#8D9F89', icon: 'check' };
      case constant.ProjectModule.TimelineStep.Status.Current:
        return { color: '#007DBA', icon: 'access_time' };
      case constant.ProjectModule.TimelineStep.Status.Upcoming:
        return { color: '#4EA8D4', icon: 'event' };
    }
  });
</script>
<template>
  <button
    class="timeline-item"
    :class="{ completed: props.item.status === constant.ProjectModule.TimelineStep.Status.Completed }"
    @click="emit('see-more', props.item.id)"
    :aria-label="
      t('aria-label.open.timeline-item', {
        title: getTranslation(props.item, 'title', props.languageEnabled),
      })
    "
  >
    <div class="timeline-item__title">
      {{ getTranslation(props.item, 'title', props.languageEnabled) }}
    </div>
    <div class="timeline-item__content">
      <div
        ref="contentRef"
        class="timeline-item__content-text"
        :class="{ noEllipsis: props.isModal }"
        :style="{
          '--max-lines': dynamicMaxLines,
          '--line-height': 1.4,
        }"
        v-html="getTranslation(props.item, 'content', props.languageEnabled)"
      ></div>
      <button
        v-if="showSeeMore && !props.isModal"
        class="timeline-item__see-more-btn"
        type="button"
        @click.stop="handleSeeMoreClick"
      >
        {{ t('buttons.see-more') }}
      </button>
    </div>
    <div class="timeline-item__status">
      <q-separator />
      <div class="timeline-item__status-info">
        <circle-icon
          :color="iconMap(props.item.status).value.color"
          :icon="iconMap(props.item.status).value.icon"
          :variant="props.item.status === constant.ProjectModule.TimelineStep.Status.Current ? 'filled' : 'outlined'"
          :aria-label="t('aria-label.timeline-step.status', { status: statusText })"
        />
        <div v-if="props.item.status === constant.ProjectModule.TimelineStep.Status.Completed">
          {{ t('project-module.timeline.status.completed') }}
        </div>
        <div v-if="props.item.status === constant.ProjectModule.TimelineStep.Status.Current">
          {{ t('project-module.timeline.status.current') }}
        </div>
        <div v-if="props.item.status === constant.ProjectModule.TimelineStep.Status.Upcoming">
          {{ t('project-module.timeline.status.upcoming') }}
        </div>
      </div>
    </div>
  </button>
</template>
<style lang="scss">
.timeline-item {
  background-color: $color-neutral-white;
  border-radius: 8px;
  padding: $space-lg;
  display: flex;
  flex-direction: column;
  height: calc(100% - #{2 * $space-lg});
  overflow: hidden;
  border: none;
  text-align: start;
  width: min(100%, 600px);

  .icon-container {
    margin: 4px;
  }

  &:hover {
    box-shadow: 0 6px 10px 0 rgba($color-neutral-grey-400-50, 0.2);
    transition: all 200ms ease;
    cursor: pointer;
  }

  &__title {
    display: flex;
    justify-content: center;
    font-size: $small-title;
    font-weight: bold;
    line-height: 1.2;
    margin-bottom: $space-sm;
    flex-shrink: 0;
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 1;
    min-height: 0;
    overflow: hidden;

    &-text {
      display: -webkit-box;
      overflow: hidden;
      -webkit-line-clamp: var(--max-lines, 5);
      line-clamp: var(--max-lines, 5);
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      word-wrap: break-word;
      word-break: break-word;
      line-height: var(--line-height, 1.4);
      max-height: calc(var(--line-height, 1.4) * var(--max-lines, 5) * 1em);
      flex-grow: 1;
      flex-shrink: 1;

      // Normalize HTML element spacing within the content
      p,
      ul,
      ol,
      li,
      div,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin: 0;
        padding: 0;
        line-height: inherit;
      }

      ul,
      ol {
        margin-left: 1.2em;
        margin-bottom: 0.3em;

        &:last-child {
          margin-bottom: 0;
        }
      }

      li {
        margin-bottom: 0.2em;

        &:last-child {
          margin-bottom: 0;
        }
      }

      p {
        margin-bottom: 0.3em;

        &:last-child {
          margin-bottom: 0;
        }
      }

      &.noEllipsis {
        display: block;
        -webkit-line-clamp: none;
        line-clamp: none;
        max-height: none;
        overflow-y: auto;
        padding-bottom: $space-sm;
      }
    }
  }

  &__see-more-btn {
    background: none;
    border: none;
    color: $color-primary;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 500;
    text-align: left;
    padding: $space-xs 0;
    margin-top: $space-xs;
    text-decoration: underline;
    flex-shrink: 0;

    &:hover {
      color: $color-primary-darken;
      text-decoration: none;
    }
  }

  &__status {
    flex-shrink: 0;
    margin-top: auto;
    width: 100%;

    &-info {
      display: flex;
      justify-content: center;
      padding: $space-md 0 0 0;
      align-items: center;
      gap: $space-sm;
    }
  }

  &.completed {
    background-color: $color-neutral-grey-blue-150;
  }
}

@media screen and (max-width: $breakpoint-lg) {
  .timeline-item__content-text {
    -webkit-line-clamp: var(--max-lines, 5);
    line-clamp: var(--max-lines, 5);
    max-height: calc(var(--line-height, 1.4) * var(--max-lines, 5) * 1em);
  }
}

@media screen and (max-width: $breakpoint-md) {
  .timeline-item__content-text {
    -webkit-line-clamp: var(--max-lines, 5);
    line-clamp: var(--max-lines, 5);
    max-height: calc(var(--line-height, 1.4) * var(--max-lines, 5) * 1em);
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .timeline-item__content-text {
    -webkit-line-clamp: var(--max-lines, 5);
    line-clamp: var(--max-lines, 5);
    max-height: calc(var(--line-height, 1.4) * var(--max-lines, 5) * 1em);
  }
}
</style>
